import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef } from "react"

import type { DashboardSpec } from "../dashboard/catalog"

import { createEventStore, type EventStore } from "./event-store"
import { registerActionObserver } from "../dashboard/action-observer"

/**
 * Context binding a single EventStore to a DashboardRenderer subtree.
 * The panel reads from this context; renderer components (and any
 * future stream taps) push into it.
 */
interface DashDevtoolsContextValue {
  eventStore: EventStore
  /** Current spec — kept in a ref so it doesn't re-render the whole tree. */
  getCurrentSpec: () => DashboardSpec | null
}

const DashDevtoolsContext = createContext<DashDevtoolsContextValue | null>(null)

interface DashDevtoolsProviderProps {
  children: ReactNode
  /**
   * Live spec. Updated externally — the provider tracks it via a ref so
   * subscribers can read it on demand without stale closures.
   */
  spec: DashboardSpec | null
  /** Optional override for the singleton store, mainly for tests. */
  eventStore?: EventStore
  /**
   * If false, the action-observer subscription is skipped. Useful when
   * mounting the provider outside of an interactive dashboard context.
   */
  observeActions?: boolean
}

/**
 * Wraps a DashboardRenderer subtree and provides the devtools event
 * store. Subscribes to {@link registerActionObserver} so action events
 * land in the buffer.
 */
export function DashDevtoolsProvider({
  children,
  spec,
  eventStore,
  observeActions = true,
}: DashDevtoolsProviderProps) {
  const storeRef = useRef<EventStore | null>(null)
  if (storeRef.current === null) {
    storeRef.current = eventStore ?? createEventStore()
  }
  const specRef = useRef<DashboardSpec | null>(spec)
  specRef.current = spec

  const value = useMemo<DashDevtoolsContextValue>(
    () => ({
      eventStore: storeRef.current!,
      getCurrentSpec: () => specRef.current,
    }),
    []
  )

  // Record a spec-changed event whenever the incoming spec reference
  // changes. JSON-serialise the relevant slice once so the store doesn't
  // hold a live reference (which would race with subsequent edits).
  useEffect(() => {
    if (!spec) return
    let serialised: string
    try {
      serialised = JSON.stringify(spec, null, 2)
    } catch (err) {
      // Circular spec — record a marker instead of crashing. Surface in
      // dev so a spec author who accidentally writes a self-reference
      // sees why their devtools timeline shows the marker instead of
      // the real serialisation. Suppressed in prod to keep noisy specs
      // from spamming end-user consoles.
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "[dash] devtools cannot serialise spec — circular or non-serialisable value",
          err
        )
      }
      serialised = "<unserialisable spec>"
    }
    value.eventStore.record({ kind: "spec-changed", at: Date.now(), specJson: serialised })
  }, [spec, value.eventStore])

  useEffect(() => {
    if (!observeActions) return
    const unsub = registerActionObserver({
      onDispatch: (info) =>
        value.eventStore.record({ kind: "action-dispatched", at: info.at, info }),
      onSettle: (info) => value.eventStore.record({ kind: "action-settled", at: info.at, info }),
    })
    return unsub
  }, [observeActions, value.eventStore])

  return <DashDevtoolsContext.Provider value={value}>{children}</DashDevtoolsContext.Provider>
}

/**
 * Read the active devtools context. Returns `null` when no provider is
 * mounted (in production, this is the typical case).
 */
export function useDashDevtools(): DashDevtoolsContextValue | null {
  return useContext(DashDevtoolsContext)
}

/**
 * Heuristic for whether the devtools panel should mount in this
 * session. Controlled by `?debug=dash` on the URL OR
 * `localStorage["dash:devtools"] === "1"`. Returns `false` during SSR.
 */
export function shouldEnableDashDevtools(): boolean {
  if (typeof window === "undefined") return false
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get("debug") === "dash") return true
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (window.localStorage?.getItem("dash:devtools") === "1") return true
  } catch {
    // Privacy mode / sandboxed iframes can throw on storage access.
    return false
  }
  return false
}
